import { graphql, useStaticQuery } from "gatsby";
import { getStoryblokLanguage } from "../../config/storyblok-config";
import { useAppState } from "../context/app/context";

interface StoryblokProuctVariantImage {
	filename: string;
	alt?: string;
}
interface StoryblokProductVariant {
	path: string;
	images?: StoryblokProuctVariantImage[];
}
export type StoryblokProductVariants = Record<string, StoryblokProductVariant>;

export const useStoryblokProductVariants = (
	ids: string[],
): Partial<StoryblokProductVariants> => {
	const { siteConfig } = useAppState();

	const allStoryblokProductVariants =
		useStaticQuery<Queries.StoryblokProductVariantQuery>(graphql`
			query StoryblokProductVariant {
				allStoryblokEntry(
					filter: { field_component: { eq: "product_variant" } }
				) {
					nodes {
						lang
						field_commercetools_variant_id_string
						field_commercetools_variant_dev_id_string
						full_slug
						translated_slugs {
							path
							lang
						}
						product_variant_images {
							stores
							image {
								filename
								alt
							}
						}
					}
				}
			}
		`).allStoryblokEntry.nodes.filter(
			(variant) => variant.lang === getStoryblokLanguage(),
		);

	const mappedProductVariants =
		allStoryblokProductVariants.reduce<StoryblokProductVariants>(
			(mappedProductVariants, productVariant) => {
				const identifier =
					process.env.GATSBY_COMMERCETOOLS_PROJECT_KEY === "babybjorn-dev"
						? productVariant.field_commercetools_variant_dev_id_string
						: productVariant.field_commercetools_variant_id_string;

				if (identifier) {
					const path = productVariant.full_slug;
					if (!path) {
						return mappedProductVariants;
					}

					const images = productVariant.product_variant_images
						?.filter((variantImage) =>
							variantImage?.stores?.includes(siteConfig.store),
						)
						.map<StoryblokProuctVariantImage>((image) => {
							return {
								filename: image?.image?.filename || "",
								alt: image?.image?.alt || "",
							};
						});

					mappedProductVariants[identifier] = { path, images };
				}

				return mappedProductVariants;
			},
			{},
		);

	return ids.reduce<StoryblokProductVariants>(
		(storyblokProductVariants, id) => {
			if (id in mappedProductVariants) {
				storyblokProductVariants[id] = mappedProductVariants[id]!;
			}

			return storyblokProductVariants;
		},
		{},
	);
};
